<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import VuePictureCropper, { cropper } from 'vue-picture-cropper'
import type { CropperInstance, PresetModeOptions } from 'vue-picture-cropper'
import type Cropper from 'cropperjs'
import Dialog from '../Dialog.vue'
import Button from '../Button.vue'
import { useDisplay } from 'vuetify'

type CropperOptions = Cropper.Options
type ContainerData = Cropper.ContainerData
type CanvasData = Cropper.CanvasData
type ImageData = Cropper.ImageData
type CropBoxData = Cropper.CropBoxData

/** Extensão tipada do zoomTo com pivot (x,y), sem usar any */
type ZoomPivot = { x: number; y: number }
type CropperWithPivot = CropperInstance & {
  zoomTo?: (ratio: number, pivot?: ZoomPivot) => void
}

const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

/** helpers */
function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max)
}
function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

/** props/emit */
const props = defineProps<{
  open: boolean
  image?: string
  type?: 'circle' | 'square'
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', value: string | null): void
  (e: 'update:dialog', value: boolean): void
  (e: 'update:image', value?: string): void
}>()

/** dialog v-model */
const internalDialogOpen = ref<boolean>(props.open)
watch(
  () => props.open,
  (v) => (internalDialogOpen.value = v)
)
watch(internalDialogOpen, (v) => emit('update:dialog', v))

/** imagem local + key para forçar re-render do cropper */
const localImage = ref<string | undefined>(props.image)
const imageKey = ref(0)
const imgSrc = computed<string | undefined>({
  get: () => localImage.value,
  set: (val?: string) => {
    localImage.value = val
    emit('update:image', val)
    imageKey.value++
  },
})
watch(
  () => props.image,
  (val) => {
    localImage.value = val
    imageKey.value++
  }
)

/** estado de zoom (slider normalizado 0..1)
 *  t=0 (esquerda) => baseZoom (cover)
 *  t=1 (direita)  => maxZoom (zoom-in máximo)
 */
const baseZoom = ref(1)
const isAdjusting = ref(false)
const zoomNormalized = ref(0) // começa à ESQUERDA
const maxZoom = computed(() => Math.min(5, baseZoom.value * 2.5))
const effectiveZoom = computed(() =>
  lerp(baseZoom.value, maxZoom.value, zoomNormalized.value)
)

/** preset do recorte */
const PRESET_SIZE = 300
const presetMode = computed<PresetModeOptions>(() =>
  (props.type ?? 'square') === 'circle'
    ? { mode: 'round', width: PRESET_SIZE, height: PRESET_SIZE }
    : { mode: 'fixedSize', width: PRESET_SIZE, height: PRESET_SIZE }
)

/** opções do cropper (tipado via cropperjs) */
const options: CropperOptions = {
  viewMode: 2,
  dragMode: 'move',
  aspectRatio: 1,
  autoCrop: true,
  background: false,
  guides: true,
  movable: true,
  rotatable: true,
  scalable: false,
  zoomable: true,
  zoomOnWheel: true,
  responsive: true,
}

/** estilo do container */
const boxStyle: Record<string, string> = {
  width: '100%',
  height: '40vh',
  background: '#fff',
  margin: '0',
}

onMounted(() => {
  // reservado para futuros hooks
})

/* ---------- Algoritmo de “cover + center” tipado ---------- */

function getInst(): CropperWithPivot | undefined {
  return cropper as CropperWithPivot | undefined
}

function computeBaseZoomCover(inst: CropperWithPivot): number {
  const img: ImageData | undefined = inst.getImageData?.()
  const crop: CropBoxData | undefined = inst.getCropBoxData?.()
  if (!img || !crop || !img.naturalWidth || !img.naturalHeight) return 1

  const wRatio = crop.width / img.naturalWidth
  const hRatio = crop.height / img.naturalHeight
  const base = Math.max(wRatio, hRatio)

  return clamp(Number(base.toFixed(3)), 0.05, 5)
}

function setSquareCropBox(inst: CropperWithPivot): void {
  const container: ContainerData | undefined = inst.getContainerData?.()
  if (!container) return

  const size = Math.min(container.width, container.height)
  const left = (container.width - size) / 2
  const top = (container.height - size) / 2

  inst.setCropBoxData?.({ left, top, width: size, height: size })
}

function centerCanvas(inst: CropperWithPivot): void {
  const container: ContainerData | undefined = inst.getContainerData?.()
  const canvas: CanvasData | undefined = inst.getCanvasData?.()
  if (!container || !canvas) return

  const left = (container.width - canvas.width) / 2
  const top = (container.height - canvas.height) / 2

  inst.setCanvasData?.({ ...canvas, left, top })
}

function fitAndCenter(inst: CropperWithPivot): void {
  setSquareCropBox(inst)

  const base = computeBaseZoomCover(inst)
  baseZoom.value = base

  // aplica o zoom base (cover) e centraliza
  const container = inst.getContainerData?.()
  if (container && inst.zoomTo) {
    inst.zoomTo(baseZoom.value, {
      x: container.width / 2,
      y: container.height / 2,
    })
  } else {
    inst.zoomTo?.(baseZoom.value)
  }
  centerCanvas(inst)

  // slider começa no 0 (esquerda) representando o baseZoom
  zoomNormalized.value = 0
}

/* ---------- Controles ---------- */
const handleZoomNormalized = (v: number) => {
  zoomNormalized.value = v
  isAdjusting.value = true
  getInst()?.zoomTo?.(effectiveZoom.value)
}
const stopAdjusting = () => {
  isAdjusting.value = false
}
const reset = async () => {
  const inst = getInst()
  if (!inst) return
  isAdjusting.value = true
  inst.reset?.()
  await nextTick()

  setSquareCropBox(inst)
  await nextTick()

  fitAndCenter(inst) // recoloca baseZoom e slider em 0
  isAdjusting.value = false
}

const onReady = async () => {
  const inst = getInst()
  if (!inst) return
  setSquareCropBox(inst)
  await nextTick()
  fitAndCenter(inst)
  isAdjusting.value = false
}

/* ---------- Fundo branco ao salvar ---------- */
async function withWhiteBackground(
  srcDataUrl: string,
  outMime: 'image/png' | 'image/jpeg',
  quality?: number
): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.crossOrigin = 'anonymous'
    i.onload = () => resolve(i)
    i.onerror = (e) => reject(e)
    i.src = srcDataUrl
  })

  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return srcDataUrl

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0)

  if (outMime === 'image/jpeg') {
    return canvas.toDataURL(
      'image/jpeg',
      typeof quality === 'number' ? quality : 0.92
    )
  }
  return canvas.toDataURL('image/png')
}

const handleSave = async () => {
  const isCircle = (props.type ?? 'square') === 'circle'
  const inst = getInst()
  const original = inst?.getDataURL?.(
    isCircle ? { format: 'png' } : { format: 'jpeg', quality: 0.92 }
  )

  if (!original) {
    emit('save', null)
    return
  }

  const finalDataUrl = await withWhiteBackground(
    original,
    isCircle ? 'image/png' : 'image/jpeg',
    0.92
  )
  emit('save', finalDataUrl)
}

const onRotate = (deg: number) => {
  const inst = getInst()
  inst?.rotate?.(deg)
}
</script>

<template>
  <Dialog
    id="crop-image-dialog"
    v-model="internalDialogOpen"
    :title="$t('contacts.adjustProfilePicture')"
    class="crop-dialog"
    rounded="16px"
    :close="true"
    @close="$emit('close')"
  >
    <!-- Área de recorte -->
    <div class="px-4">
      <VuePictureCropper
        v-if="imgSrc"
        :key="imageKey"
        :img="imgSrc"
        :box-style="boxStyle"
        :options="options"
        :preset-mode="presetMode"
        :theme="{ maskColor: 'rgba(0,0,0,0.4)' }"
        @ready="onReady"
      />
      <div v-else class="w-100 text-grey-darken-1 py-6 text-center">
        {{ $t('general.noImage') }}
      </div>
    </div>

    <!-- Controles -->
    <div class="px-4 pb-2 pt-4">
      <!-- Zoom -->
      <div class="text-subtitle-2 font-weight-medium text-grey-darken-3 mb-2">
        Zoom
      </div>
      <v-slider
        id="zoom-slider"
        v-model="zoomNormalized"
        :min="0"
        :max="1"
        :step="0.01"
        track-color="#dce4d4"
        thumb-color="#8cc63f"
        color="#8cc63f"
        thumb-size="18"
        hide-details
        class="mb-6"
        @update:modelValue="handleZoomNormalized"
        @end="stopAdjusting"
      />

      <!-- Botões de ação -->
      <div class="action-row mb-6">
        <Button
          class="ctl-btn primaryOutline ctl-btn-mobile"
          variant="outlined"
          @click="onRotate(-90)"
        >
          <v-icon start icon="mdi-rotate-left" size="18" />
          <span class="btn-label btn-label--mobile-hide">
            {{ $t('general.rotate') }}
          </span>
        </Button>

        <Button
          class="ctl-btn primaryOutline ctl-btn-mobile"
          variant="outlined"
          @click="onRotate(90)"
        >
          <v-icon start icon="mdi-rotate-right" size="18" />
          <span class="btn-label btn-label--mobile-hide">
            {{ $t('general.rotate') }}
          </span>
        </Button>

        <Button
          class="ctl-btn primaryOutline ctl-btn-mobile-text"
          variant="outlined"
          @click="reset"
        >
          <v-icon v-if="!isMobile" start icon="mdi-restore" size="18" />
          <span class="btn-label">
            {{ $t('general.reset') }}
          </span>
        </Button>
      </div>

      <!-- CTA principal -->
      <Button
        id="confirm-button"
        class="w-100 primaryFlat"
        variant="flat"
        @click="handleSave"
      >
        {{ $t('general.continue') }}
      </Button>
    </div>
  </Dialog>
</template>

<style scoped>
.crop-dialog {
  width: 450px;
}

.action-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.ctl-btn {
  min-width: 0;
  height: 40px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: none;
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.2;
}

@media (max-width: 600px) {
  .crop-dialog {
    width: 270px;
  }
  .btn-label--mobile-hide {
    display: none;
  }
  .ctl-btn {
    justify-content: center;
  }
  .ctl-btn-mobile {
    max-width: 60px;
    padding-left: 25px !important;
  }
  .ctl-btn-mobile-text {
    width: 102px;
  }
  .action-row {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
  }
}
</style>
